import { assertNever, CallSheetSort, ProspectBookSort, SortDirection } from '@signalvote/shared';

/**
 * How a list is sorted: a column and a direction. The server does every sort — the Prospects book sorts its
 * whole filtered set before its cap, and a call book's order is saved on the book — so the browser only
 * says which column, never sorts rows itself.
 */
export interface ColumnSort<C extends string> {
  column: C;
  descending: boolean;
}

/**
 * The sort after a click on a column's header: a new column starts ascending, a second click flips it, a
 * third goes back to the list's default order. The default column just flips, since going back would
 * change nothing.
 */
export function nextSort<C extends string>(current: ColumnSort<C>, column: C, defaultSort: ColumnSort<C>): ColumnSort<C> {
  if (current.column !== column) return { column, descending: false };
  if (!current.descending) return { column, descending: true };
  if (defaultSort.column === column && defaultSort.descending) return { column, descending: false };
  return defaultSort;
}

/** The Prospects book's own order: largest Ask first. */
export const BOOK_DEFAULT_SORT: ColumnSort<ProspectBookSort> = { column: ProspectBookSort.Ask, descending: true };

/** The book's sort as the URL carries it (`?sort=&dir=`): unset is its default order. */
export function bookSortOf(filters: { sort?: ProspectBookSort; dir?: SortDirection }): ColumnSort<ProspectBookSort> {
  if (!filters.sort) return filters.dir ? { column: BOOK_DEFAULT_SORT.column, descending: filters.dir === SortDirection.Desc } : BOOK_DEFAULT_SORT;
  return { column: filters.sort, descending: filters.dir === SortDirection.Desc };
}

/** The URL change for a sort; the default order clears both, so a plain book's link stays plain. */
export function bookSortChanges(sort: ColumnSort<ProspectBookSort>): { sort: ProspectBookSort | null; dir: SortDirection | null } {
  if (sort.column === BOOK_DEFAULT_SORT.column && sort.descending === BOOK_DEFAULT_SORT.descending) return { sort: null, dir: null };
  return { sort: sort.column, dir: sort.descending ? SortDirection.Desc : SortDirection.Asc };
}

/** A call book's default order: largest Ask frozen when each person was added, first. */
export const CALL_BOOK_DEFAULT_ORDER: ColumnSort<CallSheetSort> = { column: CallSheetSort.Ask, descending: true };

/** A call book's order in words — the header's "Order: …" and the note after a change. */
export function callBookOrderLabel(sortBy: CallSheetSort, descending: boolean): string {
  switch (sortBy) {
    case CallSheetSort.Ask:
      return descending ? 'Largest ask first' : 'Smallest ask first';
    case CallSheetSort.Name:
      return descending ? 'Name Z–A' : 'Name A–Z';
    case CallSheetSort.Phone:
      return descending ? 'Phone number, high to low' : 'Phone number, low to high';
    case CallSheetSort.Added:
      return descending ? 'Last added first' : 'First added first';
    case CallSheetSort.FromThisBook:
      return descending ? 'Most recently called first' : 'Not called first, then longest ago';
    case CallSheetSort.AlsoOn:
      return descending ? 'On the most other books first' : 'On the fewest other books first';
    default:
      return assertNever(sortBy);
  }
}
